import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CircleCheck, ShieldCheck, Info, Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { payBooking } from "@/lib/payments";
import { fmtINR, fmtDate, fmtHour } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PolicyAckModal } from "./PolicyAckModal";

const tierFor = (service, weight) => {
  const tiers = service.tiers || [];
  for (const t of tiers) {
    if (t.max_kg === null || (weight || 0) <= t.max_kg) return t;
  }
  return tiers[tiers.length - 1];
};

const daysBetween = (start, end) => {
  if (!start) return 0;
  if (!end) return 1;
  const d = Math.round((new Date(end) - new Date(start)) / 86400000);
  return Math.max(1, d);
};

const CYCLE_TERMS =
  "Boarding runs on a 24-hour cycle that starts at your pup's check-in time (a new cycle begins every 24 hours). Standard access is 8 AM–6 PM: check in before 8 AM and early check-in charges apply (the cycle starts at 8 AM); pick up after 6 PM and late pick-up charges apply (the cycle runs until 6 PM).";

const CANCELLATION_TERMS =
  "Free cancellation or changes up to 24 hours before check-in (100% refund). Within 24 hours of check-in, 25% of the paid amount is deducted.";

const WORKING_HOURS = "Our usual working hours are 8 AM – 6 PM, Mon–Sun.";

const OUTSIDE_HOURS_NOTE =
  "Early check-in / late check-out charges apply for slots outside 8 AM – 6 PM. Our team will confirm the exact amount before finalising your booking.";

const HOUR_BUCKETS = Array.from({ length: 16 }, (_, i) => i + 6); // 6 AM – 9 PM, hourly

const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12} ${h >= 12 ? "PM" : "AM"}`;

const WHATSAPP = "918088785201";
const enquireLink = (text) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(text)}`;

const GROOMING_SLOT_LABELS = {
  "09:00": "9:00 AM", "10:30": "10:30 AM", "12:00": "12:00 PM",
  "13:30": "1:30 PM", "15:00": "3:00 PM", "16:30": "4:30 PM",
};

const hourFee = (h) => {
  const n = Number(h);
  if (Number.isNaN(n) || n < 6 || n > 21) return 0;
  if ((n >= 6 && n < 8) || (n > 18 && n <= 21)) return 200;
  return 0;
};
const DAYCARE_DUR = { half: 5, full: 10 };
const daycarePrice = (svc, weight, option) => {
  const key = option === "full" ? "full" : "half";
  const tiers = svc.tiers || [];
  const t = tiers.find((x) => x.max_kg == null || (weight || 0) <= x.max_kg) || tiers[tiers.length - 1];
  return t ? Number(t[key]) : 0;
};

// Boarding = full 24-hour blocks from check-in; any leftover partial day is billed as day care (half <=5h, else full).
const boardingCompute = (svc, it, chosen, daycareSvc) => {
  const ciH = parseInt(String(it.checkin_time || "08:00").split(":")[0], 10);
  const coH = parseInt(String(it.checkout_time || "08:00").split(":")[0], 10);
  const ci = new Date(`${it.start_date}T00:00:00`);
  ci.setHours(ciH, 0, 0, 0);
  const co = new Date(`${it.end_date || it.start_date}T00:00:00`);
  co.setHours(coH, 0, 0, 0);
  const totalH = (co - ci) / 3600000;
  let nights = Math.max(0, Math.floor(totalH / 24));
  const leftover = totalH - nights * 24;
  let leftoverOpt = leftover > 0.0001 ? (leftover <= 5 ? "half" : "full") : null;
  if (nights === 0) { nights = 1; leftoverOpt = null; }
  const rates = chosen.map((p) => tierFor(svc, p.weight_kg).price);
  const shared = it.share_kennel === true && rates.length >= 2;
  const order = rates.map((_, i) => i).sort((a, b) => rates[b] - rates[a]);
  const discounted = new Set();
  if (shared) order.forEach((idx, rank) => { if (rank === 1 || rank === 2) discounted.add(idx); });
  const perPup = chosen.map((p, i) => {
    const base = tierFor(svc, p.weight_kg).price;
    const nightRate = discounted.has(i) ? Math.round(base * 0.6) : base;
    const dc = leftoverOpt && daycareSvc ? daycarePrice(daycareSvc, p.weight_kg, leftoverOpt) : 0;
    return { pup: p, nightRate, nights, nightAmt: nightRate * nights, dc, leftoverOpt, discounted: discounted.has(i) };
  });
  const total = perPup.reduce((s, x) => s + x.nightAmt + x.dc, 0);
  return { nights, leftoverOpt, perPup, total };
};
const dayTimingFee = (ci, option) => {
  const h = Number(String(ci || "09:00").split(":")[0]);
  return hourFee(h) + hourFee(h + (DAYCARE_DUR[option] || 10));
};
const nightTimingFee = (ci, co) =>
  hourFee(Number(String(ci || "11:00").split(":")[0])) + hourFee(Number(String(co || "11:00").split(":")[0]));

const isOutsideHours = (t) => {
  const h = Number(String(t || "").split(":")[0]);
  return !Number.isNaN(h) && (h < 8 || h > 18);
};

const HourSelect = ({ testid, value, onChange }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={onChange}
    className="mt-1.5 block rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink focus:border-bark focus:outline-none"
  >
    {HOUR_BUCKETS.map((h) => {
      const v = `${String(h).padStart(2, "0")}:00`;
      return (
        <option key={v} value={v}>
          {hourLabel(h)}{h < 8 ? " (early)" : h > 18 ? " (late)" : ""}
        </option>
      );
    })}
  </select>
);

const Book = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pups, setPups] = useState([]);
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedPups, setSelectedPups] = useState([]);
  const [items, setItems] = useState({});
  const [notes, setNotes] = useState("");
  const [step, setStep] = useState("select");
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [policyAcks, setPolicyAcks] = useState([]);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [slotData, setSlotData] = useState({}); // service_id -> { date, options:[{time,label,available}] }

  const loadSlots = async (svcId, date) => {
    if (!date) return;
    try {
      const r = await api.get(`/grooming/slots?date=${date}`);
      setSlotData((cur) => ({ ...cur, [svcId]: { date, options: r.data || [] } }));
    } catch {
      setSlotData((cur) => ({ ...cur, [svcId]: { date, options: [] } }));
    }
  };

  useEffect(() => {
    Promise.all([api.get("/pups"), api.get("/services"), api.get("/addons"), api.get("/me/policy-acks")])
      .then(([p, s, a, ack]) => {
        const activePups = (p.data || []).filter((x) => !x.archived);
        setPups(activePups);
        setServices(s.data);
        setAddons(a.data);
        setPolicyAcks(ack.data?.acks || []);
        if (activePups.length === 1) setSelectedPups([activePups[0].pup_id]);
      })
      .catch(() => toast.error("Could not load booking form"));
  }, []);

  const singlePup = pups.length === 1;

  const togglePup = (id) => {
    setSelectedPups((cur) => {
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      if (!next.includes(id)) {
        setItems((its) => {
          const cleaned = {};
          Object.entries(its).forEach(([sid, it]) => {
            const pupIds = it.pup_ids.filter((p) => p !== id);
            const addonMap = {};
            Object.entries(it.addons || {}).forEach(([aid, pids]) => {
              const kept = pids.filter((p) => pupIds.includes(p));
              if (kept.length > 0) addonMap[aid] = kept;
            });
            cleaned[sid] = { ...it, pup_ids: pupIds, addons: addonMap };
          });
          return cleaned;
        });
      }
      return next;
    });
  };

  const toggleService = (svc) =>
    setItems((cur) => {
      const next = { ...cur };
      if (next[svc.service_id]) delete next[svc.service_id];
      else
        next[svc.service_id] = {
          start_date: "",
          end_date: "",
          option: svc.category === "day-care" ? "full" : null,
          checkin_time: svc.category === "day-care" ? "08:00" : "08:00",
          checkout_time: "08:00",
          share_kennel: null,
          pup_ids: [...selectedPups],
          addons: {},
          slots: {},
        };
      return next;
    });

  const setField = (svcId, key, value) =>
    setItems((cur) => ({ ...cur, [svcId]: { ...cur[svcId], [key]: value } }));

  const setGroomingDate = (svcId, date) => {
    setItems((cur) => ({ ...cur, [svcId]: { ...cur[svcId], start_date: date, slots: {} } }));
    loadSlots(svcId, date);
  };

  const toggleSlot = (svcId, pupId, time) =>
    setItems((cur) => {
      const it = cur[svcId];
      const slots = { ...(it.slots || {}) };
      if (slots[pupId] === time) delete slots[pupId];
      else slots[pupId] = time;
      return { ...cur, [svcId]: { ...it, slots } };
    });

  const toggleItemPup = (svcId, pupId) =>
    setItems((cur) => {
      const it = cur[svcId];
      const has = it.pup_ids.includes(pupId);
      const pupIds = has ? it.pup_ids.filter((p) => p !== pupId) : [...it.pup_ids, pupId];
      const addonMap = {};
      Object.entries(it.addons || {}).forEach(([aid, pids]) => {
        const kept = pids.filter((p) => pupIds.includes(p));
        if (kept.length > 0) addonMap[aid] = kept;
      });
      const slots = {};
      Object.entries(it.slots || {}).forEach(([pid, t]) => {
        if (pupIds.includes(pid)) slots[pid] = t;
      });
      return { ...cur, [svcId]: { ...it, pup_ids: pupIds, addons: addonMap, slots } };
    });

  const toggleAddon = (svcId, addonId) =>
    setItems((cur) => {
      const it = cur[svcId];
      const map = { ...(it.addons || {}) };
      if (map[addonId]) delete map[addonId];
      else map[addonId] = [...it.pup_ids];
      return { ...cur, [svcId]: { ...it, addons: map } };
    });

  const toggleAddonPup = (svcId, addonId, pupId) =>
    setItems((cur) => {
      const it = cur[svcId];
      const sel = (it.addons || {})[addonId] || [];
      const next = sel.includes(pupId) ? sel.filter((p) => p !== pupId) : [...sel, pupId];
      const map = { ...(it.addons || {}) };
      if (next.length === 0) delete map[addonId];
      else map[addonId] = next;
      return { ...cur, [svcId]: { ...it, addons: map } };
    });

  const chosenPups = pups.filter((p) => selectedPups.includes(p.pup_id));
  const activeItems = Object.entries(items).filter(([, it]) => it);
  const removeItem = (svcId) =>
    setItems((cur) => {
      const n = { ...cur };
      delete n[svcId];
      return n;
    });

  const missingChart = pups.filter(
    (p) => selectedPups.includes(p.pup_id) && !(p.documents || []).some((d) => d.kind === "vaccination")
  );
  const blockers = [];
  if (user?.id_verified === "rejected")
    blockers.push("Your photo ID couldn't be verified — re-upload it from your account dashboard to book again.");
  else if (!user?.photo_id)
    blockers.push("Upload your photo ID from your account dashboard (top banner) — bookings need an ID on file.");
  if (missingChart.length > 0)
    blockers.push(
      `Upload the vaccination chart for ${missingChart.map((p) => p.name).join(", ")} — tap the pup card on your dashboard, then open Documents.`
    );
  const bookingBlocked = blockers.length > 0;

  const estimate = useMemo(() => {
    let total = 0;
    const daycareSvc = services.find((s) => s.slug === "day-care");
    services.forEach((svc) => {
      const it = items[svc.service_id];
      if (!it || !it.start_date) return;
      const cat = svc.category;
      const chosen = it.pup_ids.map((pid) => pups.find((p) => p.pup_id === pid)).filter(Boolean);
      const svcAddons = addons.filter((a) => (a.service_slug || "") === svc.slug);
      if (cat === "day-care") {
        chosen.forEach((p) => { total += daycarePrice(svc, p.weight_kg, it.option); });
        total += dayTimingFee(it.checkin_time, it.option);
      } else if (cat === "boarding") {
        const b = boardingCompute(svc, it, chosen, daycareSvc);
        total += b.total;
        total += nightTimingFee(it.checkin_time, it.checkout_time);
      } else {
        chosen.forEach((p) => { total += tierFor(svc, p.weight_kg).price; });
      }
      Object.entries(it.addons || {}).forEach(([aid, pids]) => {
        const addon = svcAddons.find((a) => a.addon_id === aid);
        if (!addon || pids.length === 0) return;
        total += addon.price * pids.length;
      });
    });
    return { total, advance: Math.ceil(total / 2) };
  }, [items, services, pups, addons]);

  const itemValid = (svc, it) => {
    if (!it.start_date || it.pup_ids.length === 0) return false;
    if (svc.category === "boarding") {
      if (!it.end_date || it.end_date <= it.start_date) return false;
      if (it.pup_ids.length >= 2 && typeof it.share_kennel !== "boolean") return false;
      const h = Number(String(it.checkin_time).split(":")[0]);
      const h2 = Number(String(it.checkout_time).split(":")[0]);
      if (h < 6 || h > 21 || h2 < 6 || h2 > 21) return false;
    }
    if (svc.category === "day-care") {
      if (!["half", "full"].includes(it.option)) return false;
      const h = Number(String(it.checkin_time).split(":")[0]);
      if (h < 6 || h > 21) return false;
    }
    if (svc.category === "grooming") {
      if (it.pup_ids.some((pid) => !(it.slots || {})[pid])) return false;
    }
    return true;
  };

  const estimateDetail = useMemo(() => {
    const out = [];
    const daycareSvc = services.find((s) => s.slug === "day-care");
    services.forEach((svc) => {
      const it = items[svc.service_id];
      if (!it || !it.start_date) return;
      const cat = svc.category;
      const chosen = it.pup_ids.map((pid) => pups.find((p) => p.pup_id === pid)).filter(Boolean);
      if (chosen.length === 0) return;
      const svcAddons = addons.filter((a) => (a.service_slug || "") === svc.slug);
      const lines = [];
      let sub = 0;
      if (cat === "day-care") {
        const dl = it.option === "half" ? "Half day" : "Full day";
        chosen.forEach((p) => {
          const amt = daycarePrice(svc, p.weight_kg, it.option);
          sub += amt;
          lines.push({ label: `${p.name} · ${dl}`, amount: amt });
        });
        const tf = dayTimingFee(it.checkin_time, it.option);
        if (tf > 0) { sub += tf; lines.push({ label: "Early/late access fee", amount: tf }); }
      } else if (cat === "boarding") {
        const b = boardingCompute(svc, it, chosen, daycareSvc);
        b.perPup.forEach((x) => {
          if (x.nights > 0) {
            sub += x.nightAmt;
            lines.push({ label: `${x.pup.name} · ${x.nights} night${x.nights > 1 ? "s" : ""} × ${fmtINR(x.nightRate)}${x.discounted ? " (40% kennel-share)" : ""}`, amount: x.nightAmt });
          }
          if (x.dc > 0) {
            sub += x.dc;
            lines.push({ label: `${x.pup.name} · ${x.leftoverOpt === "half" ? "Half" : "Full"} day care (partial day)`, amount: x.dc });
          }
        });
        const tf = nightTimingFee(it.checkin_time, it.checkout_time);
        if (tf > 0) { sub += tf; lines.push({ label: "Early/late access fee", amount: tf }); }
      } else {
        chosen.forEach((p) => {
          const rate = tierFor(svc, p.weight_kg).price;
          sub += rate;
          const slot = cat === "grooming" ? (it.slots || {})[p.pup_id] : null;
          const slotTxt = slot ? ` · ${GROOMING_SLOT_LABELS[slot] || slot}` : "";
          lines.push({ label: `${p.name}${slotTxt} · ${fmtINR(rate)}`, amount: rate });
        });
      }
      const addonLines = [];
      Object.entries(it.addons || {}).forEach(([aid, pids]) => {
        const addon = svcAddons.find((a) => a.addon_id === aid);
        if (!addon || pids.length === 0) return;
        const amt = addon.price * pids.length;
        sub += amt;
        addonLines.push({ label: `${addon.name} × ${pids.length} pup${pids.length > 1 ? "s" : ""}`, amount: amt });
      });
      const dateLabel = cat === "boarding" ? `${fmtDate(it.start_date)} → ${fmtDate(it.end_date)}` : fmtDate(it.start_date);
      out.push({ service_id: svc.service_id, name: svc.name, dateLabel, lines, addonLines, subtotal: sub });
    });
    return out;
  }, [items, services, pups, addons]);

  const canReview =
    selectedPups.length > 0 &&
    activeItems.length > 0 &&
    !bookingBlocked &&
    activeItems.every(([id, it]) => itemValid(services.find((s) => s.service_id === id), it));

  const steps = singlePup ? ["services", "review"] : ["pups", "services", "review"];
  const current = steps[Math.min(wizardStep, steps.length - 1)];
  const canContinuePups = selectedPups.length > 0;
  const goBack = () => setWizardStep((s) => Math.max(0, s - 1));
  const goNext = () => setWizardStep((s) => Math.min(steps.length - 1, s + 1));

  const pendingPolicyServices = useMemo(
    () =>
      activeItems
        .map(([sid]) => services.find((s) => s.service_id === sid))
        .filter((s) => s && s.policy && !policyAcks.includes(s.slug)),
    [activeItems, services, policyAcks]
  );

  const handlePayClick = () => {
    if (pendingPolicyServices.length > 0) {
      setPolicyOpen(true);
      return;
    }
    createBooking();
  };

  const agreePolicies = async () => {
    setBusy(true);
    try {
      let latest = policyAcks;
      for (const svc of pendingPolicyServices) {
        const r = await api.post("/me/policy-acks", { service_slug: svc.slug });
        latest = r.data?.acks || latest;
      }
      setPolicyAcks(latest);
      setPolicyOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save agreement");
      setBusy(false);
      return;
    }
    setBusy(false);
    createBooking();
  };

  const createBooking = async () => {
    setBusy(true);
    try {
      const payload = {
        pup_ids: selectedPups,
        notes,
        items: activeItems.map(([service_id, it]) => ({
          service_id,
          start_date: it.start_date,
          end_date: it.end_date || it.start_date,
          option: it.option || null,
          checkin_time: it.checkin_time || "11:00",
          checkout_time: it.checkout_time || "11:00",
          share_kennel: it.share_kennel === true,
          pup_ids: it.pup_ids,
          slots: Object.entries(it.slots || {}).map(([pup_id, time]) => ({ pup_id, time })),
          addons: Object.entries(it.addons || {}).map(([addon_id, pup_ids]) => ({ addon_id, pup_ids })),
        })),
      };
      const r = await api.post("/bookings", payload);
      setBooking(r.data);
      setStep("pay");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create booking");
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const res = await payBooking({
        bookingId: booking.booking_id,
        kind: "advance",
        user,
        onDemo: async (payment) => {
          toast.info(`Demo mode — Razorpay keys not configured yet. Simulating payment of ${fmtINR(payment.amount)}.`);
          return true;
        },
      });
      if (res.done) {
        setBooking(res.booking);
        setStep("done");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  if (step === "done") {
    return (
      <section data-testid="booking-success" className="pt-32 pb-28 lg:pt-40">
        <div className="mx-auto max-w-xl px-4 text-center sm:px-6">
          <Reveal>
            <span className="inline-flex rounded-full bg-emerald-50 p-5 text-emerald-600">
              <CircleCheck className="h-12 w-12" aria-hidden="true" />
            </span>
            <h1 className="mt-8 font-display text-4xl font-medium tracking-tight text-ink sm:text-5xl">
              Booking <em className="font-editorial italic text-bark">confirmed!</em>
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Your 50% advance of <span className="font-semibold text-ink">{fmtINR(booking.amount_paid)}</span> is
              recorded and your slot is reserved. Our team can now see your booking and your pup's
              profile — we'll take it from here.
            </p>
            <p className="mt-3 text-sm text-slate-500">
              Balance of {fmtINR(booking.total - booking.amount_paid)} is payable at drop-off, or
              anytime from your bookings.
            </p>
            <p className="mt-6 rounded-2xl bg-cream-dark/60 p-4 text-xs leading-relaxed text-slate-500">
              {CYCLE_TERMS} {CANCELLATION_TERMS}
            </p>
            <button
              type="button"
              data-testid="success-dashboard-button"
              onClick={() => navigate("/account")}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-ink"
            >
              Go to my account
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </Reveal>
        </div>
      </section>
    );
  }

  if (step === "pay" && booking) {
    return (
      <section data-testid="booking-payment" className="pt-32 pb-28 lg:pt-40">
        <div className="mx-auto max-w-xl px-4 sm:px-6">
          <Reveal>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
              <span className="h-px w-10 bg-bark" aria-hidden="true" />
              Reserve your slot
            </p>
            <h1 className="mt-5 font-display text-4xl font-medium tracking-tight text-ink sm:text-5xl">
              Pay <em className="font-editorial italic text-bark">50% now</em>
            </h1>
            <div className="mt-8 space-y-3 rounded-3xl border border-ink/8 bg-white p-8">
              {booking.items.map((i) => (
                <div key={i.service_id} className="text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">
                      {i.service_name} · {fmtDate(i.start_date)}
                      {i.end_date !== i.start_date && ` → ${fmtDate(i.end_date)}`}
                      {i.checkin_time && ` · check-in ${fmtHour(i.checkin_time)}`}
                      {i.checkout_time && ` · check-out ${fmtHour(i.checkout_time)}`}
                    </span>
                    <span className="font-semibold text-ink">{fmtINR(i.amount)}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {i.lines.map((l) => `${l.pup_name}${l.slot_label ? ` @ ${l.slot_label}` : ""} (${fmtINR(l.rate)}/${i.unit}${l.kennel_discount ? " — 40% kennel-share off" : ""})`).join(" · ")}
                  </p>
                  {(i.addons || []).length > 0 && (
                    <p className="mt-0.5 text-xs text-bark">
                      Add-ons: {i.addons.map((a) => `${a.name} (${fmtINR(a.rate)}${a.unit === "stay" ? "" : `/${a.unit}`}${a.units > 1 ? ` × ${a.units}` : ""}${(a.pup_names || []).length ? ` for ${a.pup_names.join(" & ")}` : ""})`).join(" · ")}
                    </p>
                  )}
                </div>
              ))}
              <div className="flex justify-between border-t border-ink/10 pt-3 text-sm">
                <span className="font-semibold text-navy">Total</span>
                <span className="font-semibold text-ink">{fmtINR(booking.total)}</span>
              </div>
              <div className="flex justify-between text-base">
                <span className="font-semibold text-navy">Advance due now (50%)</span>
                <span className="font-display text-2xl font-medium text-bark">{fmtINR(booking.advance_target)}</span>
              </div>
            </div>
            {booking.items.some((i) => isOutsideHours(i.checkin_time) || isOutsideHours(i.checkout_time)) && (
              <p data-testid="pay-outside-hours-note" className="mt-4 flex items-start gap-2 rounded-2xl bg-amberwarm/10 p-4 text-xs leading-relaxed text-bark">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bark" aria-hidden="true" />
                {OUTSIDE_HOURS_NOTE}
              </p>
            )}
            <p className="mt-4 text-center text-xs text-slate-400">{WORKING_HOURS}</p>
            <button
              type="button"
              data-testid="pay-advance-button"
              onClick={pay}
              disabled={busy}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-bark px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-60"
            >
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              {busy ? "Opening checkout…" : `Pay ${fmtINR(booking.advance_target)} with Razorpay`}
            </button>
            <p className="mt-4 text-center text-xs leading-relaxed text-slate-400">
              {CANCELLATION_TERMS}
            </p>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="book-page" className="pt-28 pb-44 lg:pt-36">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Reveal y={14}>
          <button
            type="button"
            onClick={() => navigate("/account")}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-navy"
          >
            <ArrowLeft className="h-4 w-4 text-bark transition-transform duration-200 group-hover:-translate-x-1" aria-hidden="true" />
            Back to my account
          </button>
          <h1 className="mt-6 font-display text-4xl font-medium tracking-tight text-ink sm:text-5xl">
            Book a <em className="font-editorial italic text-bark">service</em>
          </h1>
          <div data-testid="book-steps" className="mt-6 flex gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex-1">
                <div className={`h-1.5 rounded-full ${i <= wizardStep ? "bg-bark" : "bg-cream-dark"}`} />
                <p className={`mt-1.5 text-[10px] font-semibold uppercase tracking-wider ${i <= wizardStep ? "text-bark" : "text-slate-400"}`}>
                  {i + 1}. {s === "pups" ? "Pups" : s === "services" ? "Services" : "Review"}
                </p>
              </div>
            ))}
          </div>
        </Reveal>

        {bookingBlocked && (
          <Reveal delay={0.05}>
            <div data-testid="booking-blocked-banner" className="mt-8 rounded-[2rem] border border-amberwarm/40 bg-amberwarm/10 p-6 sm:p-7">
              <p className="text-sm font-semibold text-ink">Before you can book, we need:</p>
              <ul className="mt-2 list-disc space-y-1.5 pl-5 text-xs leading-relaxed text-slate-600">
                {blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              <button
                type="button"
                data-testid="booking-blocked-goto-account"
                onClick={() => navigate("/account")}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-cream transition-colors duration-200 hover:bg-ink"
              >
                Go to my account
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </Reveal>
        )}

        {current === "pups" && (
          <Reveal delay={0.1}>
            <h2 className="mt-10 font-display text-2xl font-medium text-ink">Who's coming?</h2>
            <div className="mt-5 flex flex-wrap gap-3">
              {pups.map((p) => (
                <button
                  key={p.pup_id}
                  type="button"
                  data-testid={`book-pup-${p.name.toLowerCase()}`}
                  onClick={() => togglePup(p.pup_id)}
                  className={`rounded-full border px-6 py-3 text-sm font-semibold transition-colors duration-200 ${
                    selectedPups.includes(p.pup_id)
                      ? "border-navy bg-navy text-cream"
                      : "border-ink/15 bg-white text-navy hover:border-navy/40"
                  }`}
                >
                  {p.name}
                  {p.weight_kg ? <span className="ml-2 opacity-60">{p.weight_kg} kg</span> : null}
                </button>
              ))}
            </div>
          </Reveal>
        )}

        {current === "services" && (
        <Reveal delay={0.15}>
          <h2 className="mt-10 font-display text-2xl font-medium text-ink">Pick services &amp; dates</h2>
          {pups.length > 1 && (
            <p className="mt-2 text-sm text-slate-500">
              Each service can be booked for one pup or both — grooming and sessions for two pups are
              slotted back to back.
            </p>
          )}
          <div className="mt-5 space-y-4">
            {services.filter((s) => ["boarding", "day-care", "grooming"].includes(s.category)).map((svc) => {
              const it = items[svc.service_id];
              const on = Boolean(it);
              const isNight = svc.category === "boarding";
              const isDay = svc.category === "day-care";
              const isGroom = svc.category === "grooming";
              const ranged = isNight;
              const timed = isNight || isDay;
              const svcAddons = addons.filter((a) => (a.service_slug || "") === svc.slug);
              return (
                <div
                  key={svc.service_id}
                  data-testid={`book-service-${svc.slug}`}
                  className={`rounded-3xl border bg-white p-6 transition-colors duration-200 ${
                    on ? "border-bark/50" : "border-ink/8"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-xl font-medium text-ink">{svc.name}</p>
                      <p className="mt-1 text-sm text-slate-500">{svc.description}</p>
                    </div>
                    <button
                      type="button"
                      data-testid={`book-toggle-${svc.slug}`}
                      onClick={() => toggleService(svc)}
                      className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                        on ? "bg-bark text-white" : "border border-ink/15 text-navy hover:border-bark hover:text-bark"
                      }`}
                    >
                      {on ? "Added" : "Add"}
                    </button>
                  </div>
                  {on && (
                    <div className="mt-5 space-y-5 border-t border-ink/8 pt-5">
                      {chosenPups.length === 0 && (
                        <p data-testid={`book-need-pup-${svc.slug}`} className="text-xs font-semibold text-bark">
                          Select your pup(s) in step 1 first.
                        </p>
                      )}
                      {chosenPups.length > 1 && (
                        <div>
                          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                            For which pups?
                          </label>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {chosenPups.map((p) => (
                              <button
                                key={p.pup_id}
                                type="button"
                                data-testid={`book-item-pup-${svc.slug}-${p.name.toLowerCase()}`}
                                onClick={() => toggleItemPup(svc.service_id, p.pup_id)}
                                className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                                  it.pup_ids.includes(p.pup_id)
                                    ? "border-bark bg-bark-soft text-bark"
                                    : "border-ink/15 bg-white text-slate-500 hover:border-navy/40"
                                }`}
                              >
                                {p.name}
                              </button>
                            ))}
                          </div>
                          {it.pup_ids.length > 1 && !timed && (
                            <p className="mt-2 text-xs text-slate-400">
                              Both pups selected — we'll schedule back-to-back slots.
                            </p>
                          )}
                          {it.pup_ids.length === 0 && (
                            <p className="mt-2 text-xs font-semibold text-bark">Select at least one pup for this service.</p>
                          )}
                        </div>
                      )}
                      {svc.unit === "night" && it.pup_ids.length >= 2 && (
                        <div>
                          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Can your pups share the same kennel?
                          </label>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              data-testid={`book-kennel-share-yes-${svc.slug}`}
                              onClick={() => setField(svc.service_id, "share_kennel", true)}
                              className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                                it.share_kennel === true
                                  ? "border-bark bg-bark-soft text-bark"
                                  : "border-ink/15 bg-white text-slate-500 hover:border-navy/40"
                              }`}
                            >
                              Yes, they can share
                            </button>
                            <button
                              type="button"
                              data-testid={`book-kennel-share-no-${svc.slug}`}
                              onClick={() => setField(svc.service_id, "share_kennel", false)}
                              className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                                it.share_kennel === false
                                  ? "border-bark bg-bark-soft text-bark"
                                  : "border-ink/15 bg-white text-slate-500 hover:border-navy/40"
                              }`}
                            >
                              No, separate kennels
                            </button>
                          </div>
                          {it.share_kennel === true && (
                            <p className="mt-2 rounded-2xl bg-emerald-50 p-3 text-xs leading-relaxed text-emerald-800">
                              40% off on the 2nd &amp; 3rd pup — applied to the lower-priced pups, so you always
                              get the best deal.
                            </p>
                          )}
                          {it.share_kennel === null && (
                            <p className="mt-2 text-xs font-semibold text-bark">
                              Pick one to continue — sharing a kennel saves you 40% on the 2nd &amp; 3rd pup.
                            </p>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-4">
                        <div>
                          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                            {isNight ? "Check-in date" : "Date"}
                          </label>
                          <input
                            type="date"
                            data-testid={`book-start-${svc.slug}`}
                            value={it.start_date}
                            onChange={(e) =>
                              isGroom
                                ? setGroomingDate(svc.service_id, e.target.value)
                                : setField(svc.service_id, "start_date", e.target.value)
                            }
                            className="mt-1.5 block rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink focus:border-bark focus:outline-none"
                          />
                        </div>
                        {isNight && (
                          <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Check-out date</label>
                            <input
                              type="date"
                              data-testid={`book-end-${svc.slug}`}
                              value={it.end_date}
                              min={it.start_date}
                              onChange={(e) => setField(svc.service_id, "end_date", e.target.value)}
                              className="mt-1.5 block rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink focus:border-bark focus:outline-none"
                            />
                          </div>
                        )}
                        {timed && (
                          <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Check-in time</label>
                            <HourSelect
                              testid={`book-checkin-${svc.slug}`}
                              value={it.checkin_time}
                              onChange={(e) => setField(svc.service_id, "checkin_time", e.target.value)}
                            />
                          </div>
                        )}
                        {isNight && (
                          <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Check-out time</label>
                            <HourSelect
                              testid={`book-checkout-${svc.slug}`}
                              value={it.checkout_time}
                              onChange={(e) => setField(svc.service_id, "checkout_time", e.target.value)}
                            />
                          </div>
                        )}
                      </div>
                      {isGroom && it.start_date && (
                        <div data-testid={`book-grooming-slots-${svc.slug}`}>
                          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Pick a 90-minute slot {it.pup_ids.length > 1 ? "for each pup" : ""}
                          </label>
                          {(() => {
                            const opts = (slotData[svc.service_id]?.options) || [];
                            const chosenForItem = it.pup_ids
                              .map((pid) => pups.find((p) => p.pup_id === pid))
                              .filter(Boolean);
                            if (chosenForItem.length === 0) {
                              return <p className="mt-2 text-xs font-semibold text-bark">Select at least one pup first.</p>;
                            }
                            const anyAvailable = opts.some((o) => o.available);
                            return (
                              <div className="mt-2 space-y-3">
                                {!anyAvailable && (
                                  <p className="rounded-2xl bg-amberwarm/10 p-3 text-xs text-bark">
                                    No open slots on this date — pick another day or enquire below.
                                  </p>
                                )}
                                {chosenForItem.map((p) => {
                                  const mine = (it.slots || {})[p.pup_id];
                                  const takenByOthers = Object.entries(it.slots || {})
                                    .filter(([pid]) => pid !== p.pup_id)
                                    .map(([, t]) => t);
                                  return (
                                    <div key={p.pup_id}>
                                      {it.pup_ids.length > 1 && (
                                        <p className="text-[11px] font-semibold text-navy">{p.name}</p>
                                      )}
                                      <div className="mt-1.5 flex flex-wrap gap-2">
                                        {opts.map((o) => {
                                          const disabled = (!o.available && o.time !== mine) || takenByOthers.includes(o.time);
                                          const on = mine === o.time;
                                          return (
                                            <button
                                              key={o.time}
                                              type="button"
                                              data-testid={`book-slot-${svc.slug}-${p.name.toLowerCase()}-${o.time.replace(":", "")}`}
                                              disabled={disabled}
                                              onClick={() => toggleSlot(svc.service_id, p.pup_id, o.time)}
                                              className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                                                on
                                                  ? "border-bark bg-bark-soft text-bark"
                                                  : disabled
                                                  ? "cursor-not-allowed border-ink/10 bg-cream-dark/40 text-slate-300 line-through"
                                                  : "border-ink/15 bg-white text-slate-500 hover:border-navy/40"
                                              }`}
                                            >
                                              {o.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                                {it.pup_ids.some((pid) => !(it.slots || {})[pid]) && (
                                  <p className="text-xs font-semibold text-bark">
                                    Pick a slot for {it.pup_ids.length > 1 ? "each pup" : "your pup"} to continue.
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                          <a
                            href={enquireLink("Hi WhatTheBark! I'd like to enquire about a grooming appointment for my pup.")}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`book-grooming-enquire-${svc.slug}`}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-bark underline underline-offset-2"
                          >
                            Prefer to enquire? Message us on WhatsApp →
                          </a>
                        </div>
                      )}
                      {isNight && (
                        <p data-testid={`book-cycle-hint-${svc.slug}`} className="flex items-start gap-1.5 text-xs leading-relaxed text-slate-500">
                          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bark" aria-hidden="true" />
                          <span>
                            {(() => {
                              const chosen = (it.pup_ids || []).map((pid) => pups.find((p) => p.pup_id === pid)).filter(Boolean);
                              if (!it.start_date || !it.end_date || chosen.length === 0) {
                                return "The 24-hour cycle starts at your check-in time — any overnight stay is billed as at least 1 night.";
                              }
                              const dcSvc = services.find((s) => s.slug === "day-care");
                              const b = boardingCompute(svc, it, chosen, dcSvc);
                              const nightTxt = `${b.nights} night${b.nights > 1 ? "s" : ""}`;
                              const extra = b.leftoverOpt ? ` + a ${b.leftoverOpt === "half" ? "half" : "full"}-day day care for the part-day` : "";
                              return `With these times you'll be billed ${nightTxt}${extra}. The 24-hour cycle starts at your check-in time.`;
                            })()}
                          </span>
                        </p>
                      )}
                      {isDay && (
                        <div data-testid={`book-daycare-option-${svc.slug}`}>
                          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Duration</label>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {(svc.options || []).map((o) => (
                              <button
                                key={o.key}
                                type="button"
                                data-testid={`book-dayopt-${svc.slug}-${o.key}`}
                                onClick={() => setField(svc.service_id, "option", o.key)}
                                className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                                  it.option === o.key ? "border-bark bg-bark-soft text-bark" : "border-ink/15 bg-white text-slate-500 hover:border-navy/40"
                                }`}
                              >
                                {o.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {timed && <p className="text-xs text-slate-400">{WORKING_HOURS}</p>}
                      {timed && (isDay ? dayTimingFee(it.checkin_time, it.option) : nightTimingFee(it.checkin_time, it.checkout_time)) > 0 && (
                        <p data-testid={`book-timing-fee-${svc.slug}`} className="flex items-start gap-2 rounded-2xl bg-amberwarm/10 p-4 text-xs leading-relaxed text-bark">
                          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bark" aria-hidden="true" />
                          A ₹200 access fee applies per early (6–8 AM) or late (6–9 PM) check-in/out. Added:{" "}
                          {fmtINR(isDay ? dayTimingFee(it.checkin_time, it.option) : nightTimingFee(it.checkin_time, it.checkout_time))}.
                        </p>
                      )}
                      {svcAddons.length > 0 && (
                        <div>
                          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Add-ons
                          </label>
                          <div className="mt-2 space-y-2.5">
                            {svcAddons.map((a) => {
                              const sel = (it.addons || {})[a.addon_id];
                              return (
                                <div key={a.addon_id}>
                                  <button
                                    type="button"
                                    data-testid={`book-addon-${svc.slug}-${a.slug}`}
                                    onClick={() => toggleAddon(svc.service_id, a.addon_id)}
                                    className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                                      sel
                                        ? "border-bark bg-bark-soft text-bark"
                                        : "border-ink/15 bg-white text-slate-500 hover:border-navy/40"
                                    }`}
                                  >
                                    {a.name} · {fmtINR(a.price)}
                                    {a.unit !== "stay" && `/${a.unit}`}
                                    {sel && ` · ${sel.length} pup${sel.length > 1 ? "s" : ""}`}
                                  </button>
                                  {sel && it.pup_ids.length > 1 && (
                                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                        For:
                                      </span>
                                      {it.pup_ids.map((pid) => {
                                        const p = pups.find((x) => x.pup_id === pid);
                                        if (!p) return null;
                                        const on = sel.includes(pid);
                                        return (
                                          <button
                                            key={pid}
                                            type="button"
                                            data-testid={`book-addon-pup-${svc.slug}-${a.slug}-${p.name.toLowerCase()}`}
                                            onClick={() => toggleAddonPup(svc.service_id, a.addon_id, pid)}
                                            className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors duration-200 ${
                                              on
                                                ? "border-bark bg-bark-soft text-bark"
                                                : "border-ink/15 bg-white text-slate-400 hover:border-navy/40"
                                            }`}
                                          >
                                            {p.name}
                                          </button>
                                        );
                                      })}
                                      <span className="text-[10px] text-slate-400">
                                        charged per pup{a.unit !== "stay" ? ` per ${a.unit}` : ""}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {ranged && (
                        <p className="flex items-start gap-2 rounded-2xl bg-cream-dark/60 p-4 text-xs leading-relaxed text-slate-500">
                          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bark" aria-hidden="true" />
                          {CYCLE_TERMS}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div data-testid="enquire-block" className="mt-6 rounded-3xl border border-ink/8 bg-cream-dark/30 p-6">
            <p className="font-display text-lg font-medium text-ink">Training, Events &amp; Vaccination</p>
            <p className="mt-1 text-sm text-slate-500">
              These are arranged with our team — tap to enquire on WhatsApp.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {services
                .filter((s) => ["training", "events", "vaccination"].includes(s.category))
                .map((s) => (
                  <a
                    key={s.service_id}
                    href={enquireLink(`Hi WhatTheBark! I'd like to enquire about ${s.name} for my pup.`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`enquire-${s.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-4 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
                  >
                    {s.name} →
                  </a>
                ))}
            </div>
          </div>
        </Reveal>
        )}

        {current === "review" && (
          <Reveal delay={0.1}>
            <h2 className="mt-10 font-display text-2xl font-medium text-ink">Review your booking</h2>
            <div data-testid="book-review" className="mt-5 space-y-4">
              {estimateDetail.map((e, idx) => (
                <div key={idx} className="rounded-3xl border border-ink/8 bg-white p-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-display text-lg font-medium text-ink">{e.name}</p>
                      <span className="text-xs text-slate-400">{e.dateLabel}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        data-testid={`review-edit-${e.service_id}`}
                        onClick={() => setWizardStep(steps.indexOf("services"))}
                        className="rounded-full border border-ink/15 px-3 py-1.5 text-[11px] font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        data-testid={`review-remove-${e.service_id}`}
                        onClick={() => removeItem(e.service_id)}
                        className="rounded-full border border-ink/15 px-3 py-1.5 text-[11px] font-semibold text-red-500 transition-colors duration-200 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {e.lines.map((l, i2) => (
                      <li key={i2} className="flex justify-between gap-3 text-slate-600">
                        <span>{l.label}</span>
                        <span className="font-medium text-ink">{fmtINR(l.amount)}</span>
                      </li>
                    ))}
                    {e.addonLines.map((l, i2) => (
                      <li key={`a${i2}`} className="flex justify-between gap-3 text-bark">
                        <span>{l.label}</span>
                        <span className="font-medium">{fmtINR(l.amount)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex justify-between border-t border-ink/8 pt-3 text-sm font-semibold text-ink">
                    <span>Subtotal</span>
                    <span>{fmtINR(e.subtotal)}</span>
                  </div>
                </div>
              ))}
              {estimateDetail.length === 0 && (
                <p className="rounded-3xl border border-dashed border-ink/15 bg-white/60 p-6 text-sm text-slate-500">
                  Nothing added yet — go back and pick a service.
                </p>
              )}
              {estimateDetail.length > 0 && (
                <div data-testid="review-grand-total" className="rounded-3xl bg-navy px-6 py-5 text-cream">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-cream/70">Total ({estimateDetail.length} service{estimateDetail.length > 1 ? "s" : ""})</span>
                    <span className="font-display text-2xl font-medium">{fmtINR(estimate.total)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-cream/60">
                    <span>Pay now (50% advance)</span>
                    <span className="font-semibold text-cream">{fmtINR(estimate.advance)}</span>
                  </div>
                </div>
              )}
            </div>

            <h3 className="mt-8 font-display text-xl font-medium text-ink">Anything we should know?</h3>
            <textarea
              data-testid="book-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Feeding times, pick-up preferences, quirks…"
              className="mt-3 w-full rounded-3xl border border-ink/15 bg-white px-5 py-4 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
            />
            <p className="mt-4 rounded-2xl bg-cream-dark/60 p-4 text-xs leading-relaxed text-slate-500">
              {CYCLE_TERMS} {CANCELLATION_TERMS}
            </p>
          </Reveal>
        )}
      </div>

      <div
        data-testid="book-action-bar"
        className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 border-t border-ink/10 bg-cream/95 px-4 py-3 backdrop-blur-xl md:bottom-0"
      >
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Estimate</p>
            <p className="font-display text-xl font-medium text-ink">
              {fmtINR(estimate.total)} <span className="text-xs font-normal text-slate-400">· {fmtINR(estimate.advance)} advance</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {wizardStep > 0 && (
              <button
                type="button"
                data-testid="book-back"
                onClick={goBack}
                className="rounded-full border border-ink/15 px-5 py-3 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
              >
                Back
              </button>
            )}
            {current === "pups" && (
              <button
                type="button"
                data-testid="book-continue"
                onClick={goNext}
                disabled={!canContinuePups}
                className="inline-flex items-center gap-2 rounded-full bg-bark px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-50"
              >
                Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {current === "services" && (
              <button
                type="button"
                data-testid="book-review-next"
                onClick={goNext}
                disabled={!canReview}
                className="inline-flex items-center gap-2 rounded-full bg-bark px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-50"
              >
                Review <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {current === "review" && (
              <button
                type="button"
                data-testid="review-booking-button"
                onClick={handlePayClick}
                disabled={!canReview || busy}
                className="inline-flex items-center gap-2 rounded-full bg-bark px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-50"
              >
                {busy ? "Creating…" : `Pay ${fmtINR(estimate.advance)}`} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>

      <PolicyAckModal
        open={policyOpen}
        services={pendingPolicyServices}
        busy={busy}
        onAgree={agreePolicies}
        onClose={() => setPolicyOpen(false)}
      />
    </section>
  );
};

export default Book;
